import { execFile } from "node:child_process";
import { promisify } from "node:util";
import type { ToolResult } from "../types.js";
import { err, okJson } from "../types.js";

const execFileAsync = promisify(execFile);

/**
 * Wall-clock ceiling for the driver-binary probe.
 *
 * `which` searches `PATH`, and a `PATH` entry on a wedged network mount makes
 * the lookup itself unbounded. This runs on an agent request path inside the
 * long-lived `skaile serve` process, so the probe must fail rather than wait.
 */
const WHICH_TIMEOUT_MS = 10_000;

// Minimal interface for WorkspacePlugin to avoid circular dep.
// The unified ConnectorManager owns both filesystem-face (mountable) and
// tool-face connectors — `listConnectors()` returns both; filesystem-face
// entries carry a `mountPath`.
interface ConnectorSummary {
  id: string;
  driver: string;
  mountPath?: string;
}
interface PluginLike {
  projectDir: string;
  connectorManager: { listConnectors(): ConnectorSummary[] } | null;
  reload(): Promise<void>;
}

/** Split a manager's connectors into mountable / tool-face groups. */
function splitConnectors(plugin: PluginLike): {
  mounts: ConnectorSummary[];
  connectors: ConnectorSummary[];
} {
  const all = plugin.connectorManager?.listConnectors() ?? [];
  return {
    mounts: all.filter((c) => typeof c.mountPath === "string"),
    connectors: all.filter((c) => typeof c.mountPath !== "string"),
  };
}

function maskSecret(value: string): string {
  if (value.length <= 4) return "****";
  return `${value.slice(0, 4)}****`;
}

function resolveEnvRef(ref: string): {
  resolved: boolean;
  value?: string;
  provider?: string;
  error?: string;
} {
  if (ref.startsWith("env:")) {
    const varName = ref.slice(4);
    const val = process.env[varName];
    if (val) return { resolved: true, value: val, provider: "env" };
    return { resolved: false, error: `env var '${varName}' is not set` };
  }
  const val = process.env[ref];
  if (val) return { resolved: true, value: val, provider: "env" };
  return { resolved: false, error: `'${ref}' not found in environment` };
}

/**
 * Return a summary of the current workspace configuration.
 *
 * Reports the active driver, provider, model, mount count, connector count,
 * and per-resource descriptors.
 *
 * @param plugin   - Plugin instance (or compatible shape) exposing managers.
 * @param driver   - Driver name (e.g. `"omp"`, `"claude-sdk"`).
 * @param provider - Provider identifier, if resolved.
 * @param model    - Model identifier, if resolved.
 *
 * @docLink packages/workspace-plugin/concepts#tool-delivery
 */
export async function workspaceInfo(
  plugin: PluginLike,
  driver: string,
  provider: string | undefined,
  model: string | undefined,
): Promise<ToolResult> {
  try {
    const { mounts, connectors } = splitConnectors(plugin);
    return okJson({
      driver,
      provider,
      model,
      mountCount: mounts.length,
      connectorCount: connectors.length,
      mounts: mounts.map((v) => ({ id: v.id, driver: v.driver, mounted: true })),
      connectors: connectors.map((c) => ({ id: c.id, adapter: c.driver })),
      projectDir: plugin.projectDir,
    });
  } catch (e: unknown) {
    return err(e instanceof Error ? e.message : String(e));
  }
}

/**
 * Test resolution of a secret reference.
 *
 * Resolves `env:<VAR>` references against `process.env`.  For other provider
 * schemes (e.g. `forge:key`) reports that a running forge/vault context is
 * required.  Returns a JSON object with `{ resolved, provider?, masked? }` —
 * the secret value is never returned in plain text.
 *
 * @param ref - Secret reference string, e.g. `"env:ANTHROPIC_API_KEY"`.
 *
 * @docLink packages/workspace-plugin/concepts#tool-delivery
 */
export async function workspaceTestSecret(ref: string): Promise<ToolResult> {
  try {
    const result = resolveEnvRef(ref);
    if (result.resolved && result.value) {
      return okJson({
        resolved: true,
        provider: result.provider,
        masked: maskSecret(result.value),
      });
    }
    if (ref.includes(":") && !ref.startsWith("env:")) {
      return okJson({
        resolved: false,
        error: `Provider '${ref.split(":")[0]}' requires a running forge/vault context. Use env: refs for standalone validation.`,
      });
    }
    return okJson({ resolved: false, error: result.error });
  } catch (e: unknown) {
    return err(e instanceof Error ? e.message : String(e));
  }
}

/**
 * Run a full health check on the workspace.
 *
 * Checks whether the driver binary is installed, then validates every
 * declared mount and connector.  Returns a JSON report with a top-level
 * `passed` boolean and per-resource `ok` flags.
 *
 * @param plugin - Plugin instance (or compatible shape) exposing managers.
 * @param driver - Driver name used to locate the binary (`omp` or `claude`).
 *
 * @docLink packages/workspace-plugin/concepts#tool-delivery
 */
export async function workspaceValidate(plugin: PluginLike, driver: string): Promise<ToolResult> {
  const report: Record<string, unknown> = {
    driver: { name: driver, installed: false },
    mounts: [] as unknown[],
    connectors: [] as unknown[],
  };

  try {
    const binaryName = driver === "omp" ? "omp" : "claude";
    // `killSignal: "SIGKILL"`: a process stuck in an uninterruptible wait on a
    // dead mount never receives SIGTERM, so a SIGTERM-based timeout would not
    // actually end it — the same reason the recipe resolver uses SIGKILL.
    await execFileAsync("which", [binaryName], {
      timeout: WHICH_TIMEOUT_MS,
      killSignal: "SIGKILL",
    });
    (report.driver as any).installed = true;
  } catch {
    (report.driver as any).installed = false;
    (report.driver as any).hint = `Install the '${driver}' binary to use this driver`;
  }

  const { mounts, connectors } = splitConnectors(plugin);
  for (const v of mounts) {
    (report.mounts as any[]).push({
      id: v.id,
      driver: v.driver,
      mounted: true,
      ok: true,
    });
  }

  for (const c of connectors) {
    (report.connectors as any[]).push({
      id: c.id,
      adapter: c.driver,
      connected: true,
      ok: true,
    });
  }

  report.passed =
    (report.driver as any).installed &&
    (report.mounts as any[]).every((v) => v.ok) &&
    (report.connectors as any[]).every((c) => c.ok);

  return okJson(report);
}

/**
 * Re-read `skaile.yaml` and reinitialise all plugin resources.
 *
 * Delegates to `plugin.reload()`.  Returns a JSON object comparing the
 * mount and connector counts before and after the reload.
 *
 * @param plugin - Plugin instance exposing a `reload()` method.
 *
 * @docLink packages/workspace-plugin/concepts#tool-delivery
 */
export async function workspaceReload(plugin: PluginLike): Promise<ToolResult> {
  try {
    const countBefore = splitConnectors(plugin);
    const before = { mounts: countBefore.mounts.length, connectors: countBefore.connectors.length };
    await plugin.reload();
    const countAfter = splitConnectors(plugin);
    const after = { mounts: countAfter.mounts.length, connectors: countAfter.connectors.length };
    return okJson({ reloaded: true, before, after });
  } catch (e: unknown) {
    return err(e instanceof Error ? e.message : String(e));
  }
}
